import "./wdyr";
import "./index.css";
import React from "react";
import { Provider } from "react-redux";
import store from "./store";
import App from "./containers/App";
import { StyledEngineProvider } from "@mui/material/styles";
import "sanitize.css";
import "./css/app.less";
import { telemetryInit } from "functions/telemetry";
import { GtmProvider } from "shared-ui/src/instrumentation/gtm";
import { BrowserRouter } from "react-router-dom";
import Auth0ProviderWithHistory from "./auth/auth0-provider-with-history";
import "shared-ui/src/muiLicense";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ClaimsProvider } from "auth/ClaimsProvider";
import { createRoot } from "react-dom/client";
import { ThemeModeProvider } from "./theme/ThemeModeProviderContext";
import { logRuntimeConfig } from "./runtimeConfig";
import MsalRedirectBridge from "./MsalRedirectBridge";
import { isEmbeddedMode } from "./utils/embeddedMode";

const target = document.getElementById("root")!;

const root = createRoot(target);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

telemetryInit();
logRuntimeConfig();

/** Returns true when the current URL matches the MSAL callback route and contains an auth response payload. */
function hasMsalRedirectResponse() {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.location.pathname !== "/auth/callback") {
    return false;
  }

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const searchParams = new URLSearchParams(window.location.search.replace(/^\?/, ""));

  const hasHashAuthResponse =
    (hashParams.has("code") || hashParams.has("error")) && hashParams.has("state");

  const hasSearchAuthResponse =
    (searchParams.has("code") || searchParams.has("error")) && searchParams.has("state");

  return hasHashAuthResponse || hasSearchAuthResponse;
}

/** Flag indicating if the URL contains an MSAL auth response on the callback route. */
const isMsalRedirect = hasMsalRedirectResponse();

if (isMsalRedirect) {
  root.render(
    <React.StrictMode>
      <MsalRedirectBridge isAuth={isMsalRedirect} />
    </React.StrictMode>
  );
} else {
  if (import.meta.env.DIST_ENV !== "prod") {
    localStorage.setItem("debug", "app-debug:*");
  }
  const appTree = (
    <StyledEngineProvider injectFirst>
      <Provider store={store}>
        <BrowserRouter>
          <Auth0ProviderWithHistory>
            <QueryClientProvider client={queryClient}>
              <ThemeModeProvider>
                <ClaimsProvider>
                  <ReactQueryDevtools />
                  <App />
                </ClaimsProvider>
              </ThemeModeProvider>
            </QueryClientProvider>
          </Auth0ProviderWithHistory>
        </BrowserRouter>
      </Provider>
    </StyledEngineProvider>
  );

  // Embedded CX (inside the CS iframe) never mounts GtmProvider at all - not just its
  // identity push. GtmProvider has no embedded-mode awareness of its own (it's shared
  // with CS), and it unconditionally injects the GTM script and starts click tracking,
  // both of which would otherwise emit into the CX property while the user is actually
  // working in CS - the cross-property double counting utils/telemetryGtm.ts's identity
  // gate alone doesn't prevent.
  root.render(
    <React.StrictMode>
      {isEmbeddedMode() ? (
        appTree
      ) : (
        <GtmProvider
          app="cx"
          distEnv={import.meta.env.DIST_ENV}
          containerId={import.meta.env.GTM_CONTAINER_ID}
          serverContainerUrl={import.meta.env.GTM_SERVER_CONTAINER_URL || undefined}
        >
          {appTree}
        </GtmProvider>
      )}
    </React.StrictMode>,
  );
}